import * as React from "react";
import { LifeBuoy, Loader2, MessageCircle, Plus, Send } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Textarea,
  toast,
} from "@eduviaos/ui-kit";
import {
  useCreateMySupportTicket,
  useMarkMySupportRead,
  useMySupportMessages,
  useMySupportRealtime,
  useMySupportTickets,
  useSendMySupportMessage,
  openMySupportFile,
  uploadMySupportFile,
  type SupportTicket,
} from "../../lib/support/api";

function formatWhen(value: string): string {
  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

export function SupportPanel({ learnerName }: { learnerName: string }) {
  const tickets = useMySupportTickets();
  const create = useCreateMySupportTicket();
  const send = useSendMySupportMessage();
  const { mutate: markRead } = useMarkMySupportRead();
  const [createOpen, setCreateOpen] = React.useState(false);
  const [active, setActive] = React.useState<SupportTicket | null>(null);
  const [subject, setSubject] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [priority, setPriority] = React.useState<"normal" | "high" | "urgent">("normal");
  const [reply, setReply] = React.useState("");
  const [replyFiles, setReplyFiles] = React.useState<File[]>([]);
  const messages = useMySupportMessages(active?.id ?? null);
  useMySupportRealtime(active?.id ?? null);

  React.useEffect(() => {
    if (!active || active.requester_unread_count === 0) return;
    markRead(active.id);
  }, [active, markRead]);

  const createTicket = async () => {
    if (!subject.trim() || !description.trim()) {
      toast.error("Add a subject and describe what you need help with.");
      return;
    }
    try {
      const ticket = await create.mutateAsync({
        subject: subject.trim(),
        body: description.trim(),
        requesterName: learnerName,
        priority,
      });
      setSubject("");
      setDescription("");
      setPriority("normal");
      setCreateOpen(false);
      setActive(ticket);
      toast.success("Your centre received the request.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not create the request.");
    }
  };

  const sendReply = async () => {
    if (!active || (!reply.trim() && replyFiles.length === 0)) return;
    const body = reply.trim();
    const chosen = replyFiles;
    setReply("");
    setReplyFiles([]);
    try {
      const attachments = [];
      for (const file of chosen) {
        attachments.push(await uploadMySupportFile(active.id, file));
      }
      await send.mutateAsync({
        ticketId: active.id,
        body,
        clientMessageId: crypto.randomUUID(),
        attachments,
      });
    } catch (error) {
      setReply(body);
      setReplyFiles(chosen);
      toast.error(error instanceof Error ? error.message : "Could not send your message.");
    }
  };

  return (
    <section className="space-y-5" aria-labelledby="support-heading">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 id="support-heading" className="font-display text-2xl font-semibold">
            Centre support
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Ask your centre for help and keep the full conversation in one place.
          </p>
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus aria-hidden="true" className="size-4" />
          New request
        </Button>
      </header>

      {tickets.isLoading ? (
        <div className="space-y-3" aria-label="Loading support requests">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="bg-muted h-28 animate-pulse rounded-2xl" />
          ))}
        </div>
      ) : tickets.isError ? (
        <Card>
          <CardContent className="p-5 text-sm">
            Could not load support requests.
            <Button variant="link" onClick={() => void tickets.refetch()}>
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : (tickets.data?.items.length ?? 0) === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center px-6 py-10 text-center">
            <LifeBuoy aria-hidden="true" className="text-muted-foreground size-9" />
            <h2 className="mt-4 font-semibold">No support requests</h2>
            <p className="text-muted-foreground mt-1 max-w-xs text-sm">
              If something at the centre needs attention, send a request here.
            </p>
            <Button className="mt-5" size="sm" onClick={() => setCreateOpen(true)}>
              Create request
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {(tickets.data?.items ?? []).map((ticket) => (
            <button
              key={ticket.id}
              type="button"
              onClick={() => setActive(ticket)}
              className="border-border/60 bg-card hover:bg-muted/30 w-full rounded-2xl border p-4 text-left shadow-sm transition-colors duration-150 active:scale-[0.99]"
            >
              <div className="flex items-start justify-between gap-3">
                <span className="font-medium">{ticket.subject}</span>
                {ticket.requester_unread_count > 0 ? (
                  <span className="bg-primary text-primary-foreground grid min-w-5 place-items-center rounded-full px-1.5 text-[10px] leading-5">
                    {ticket.requester_unread_count}
                  </span>
                ) : null}
              </div>
              <div className="text-muted-foreground mt-2 flex items-center justify-between gap-3 text-xs">
                <span className="capitalize">{ticket.status.replace("_", " ")}</span>
                <span className="tabular-nums">
                  {formatWhen(ticket.last_message_at ?? ticket.created_at)}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New support request</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="support-subject">What do you need help with?</Label>
              <Input
                id="support-subject"
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                maxLength={200}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="support-description">Details</Label>
              <Textarea
                id="support-description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                rows={5}
                maxLength={4000}
              />
            </div>
            <div className="space-y-2">
              <Label>Urgency</Label>
              <Select
                value={priority}
                onValueChange={(value) => setPriority(value as typeof priority)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button disabled={create.isPending} onClick={() => void createTicket()}>
              {create.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
              Send to centre
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet
        open={active !== null}
        onOpenChange={(open) => {
          if (!open) {
            setActive(null);
            setReply("");
          }
        }}
      >
        <SheetContent side="right" className="flex w-full flex-col p-0 sm:max-w-lg">
          <SheetHeader className="border-border/60 border-b p-5">
            <SheetTitle>{active?.subject ?? "Support request"}</SheetTitle>
            <SheetDescription>Your private conversation with the centre team.</SheetDescription>
          </SheetHeader>
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-5">
            {messages.isLoading ? (
              <Loader2 className="text-muted-foreground mx-auto mt-10 size-6 animate-spin" />
            ) : (
              (messages.data?.items ?? []).map((message) => (
                <article
                  key={message.id}
                  className="border-border/60 bg-card rounded-xl border p-4"
                >
                  <div className="flex items-center justify-between gap-3 text-xs">
                    <span className="font-medium">{message.author_name}</span>
                    <time className="text-muted-foreground" dateTime={message.created_at}>
                      {formatWhen(message.created_at)}
                    </time>
                  </div>
                  <p className="mt-2 whitespace-pre-wrap text-sm">{message.body}</p>
                  {(message.attachments ?? []).length > 0 ? (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {(message.attachments ?? []).map((file) => (
                        <Button
                          key={file.object_path}
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            void openMySupportFile(active?.id ?? "", file.object_path).catch(() =>
                              toast.error("Could not open that file."),
                            )
                          }
                        >
                          {file.filename}
                        </Button>
                      ))}
                    </div>
                  ) : null}
                  <span className="text-muted-foreground mt-2 inline-flex items-center gap-1 text-[10px]">
                    <MessageCircle aria-hidden="true" className="size-3" />
                    {message.delivery_status === "sending"
                      ? "Sending…"
                      : message.moderation_status === "visible"
                        ? "Delivered"
                        : "Under review"}
                  </span>
                </article>
              ))
            )}
          </div>
          <div className="border-border/60 space-y-3 border-t p-5">
            <Label htmlFor="support-reply">Reply</Label>
            <Textarea
              id="support-reply"
              value={reply}
              onChange={(event) => setReply(event.target.value)}
              rows={3}
              maxLength={4000}
            />
            <Label htmlFor="learner-support-files" className="cursor-pointer text-sm underline">
              Add image or document
            </Label>
            <input
              id="learner-support-files"
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif,application/pdf,text/plain,.doc,.docx,.xlsx"
              multiple
              className="sr-only"
              onChange={(event) => {
                const picked = Array.from(event.target.files ?? []);
                setReplyFiles((current) => [...current, ...picked].slice(0, 4));
                event.target.value = "";
              }}
            />
            {replyFiles.map((file) => (
              <span key={`${file.name}-${file.size}`} className="text-muted-foreground text-xs">
                {file.name}
              </span>
            ))}
            <Button
              className="w-full"
              disabled={(!reply.trim() && replyFiles.length === 0) || send.isPending}
              onClick={() => void sendReply()}
            >
              <Send aria-hidden="true" className="size-4" />
              {send.isPending ? "Sending…" : "Send message"}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </section>
  );
}
